import { useMemo } from 'react';
import { CEProductCustomizationPreview } from '../../bitf-customization-engine/CEProductCustomizationPreview';
import { CEProductCustomizationForm } from '../product-customization-form/CEProductCustomizationForm';
import CEAddToCartButton from '../add-to-cart-button/CEAddToCartButton';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';
import CEProductCustomizationPrice from '../product-customization-price/CEProductCustomizationPrice';

export default function CEProductCustomizationDrawer({
  product,
  storeName = 'product-customization-drawer',
  watchStoreChanges = false,
  language,
  locale,
  country,
  market,
  localization,
  productHandle,
  productType,
  playerHandle,
  competitionHandle,
  showProductImage = true,
  isAlwaysVisible = true,
  currency,
}) {
  const normalizedProduct = product ?? null;
  const normalizedProductHandle = productHandle ?? normalizedProduct?.handle ?? null;
  const normalizedProductType = (
    productType ??
    normalizedProduct?.metafields?.custom?.product_type ??
    'jersey'
  ).toLowerCase();

  const resolvedLocalization = useMemo(() => {
    const targetLocale = localization?.locale ?? locale ?? language ?? null;
    const targetCountry = localization?.country ?? country ?? null;
    const targetMarket = localization?.market ?? market ?? null;

    if (targetLocale || targetCountry || targetMarket) {
      return {
        locale: targetLocale ?? undefined,
        country: targetCountry ?? undefined,
        market: targetMarket ?? undefined,
      };
    }

    return undefined;
  }, [localization, locale, language, country, market]);

  const customizationHookConfig = useMemo(
    () => ({
      storeName,
      watchStoreChanges,
      language: language ?? locale ?? resolvedLocalization?.locale,
      country,
      market,
      localization: resolvedLocalization,
    }),
    [storeName, watchStoreChanges, language, locale, resolvedLocalization, country, market],
  );

  // Get customization hook for reset functionality
  const { resetCustomization } = useCEProductCustomization({
    storeName,
    watchStoreChanges: false,
    localization: resolvedLocalization,
  });

  // Reset function for after successful add to cart
  const handleAddToCartSuccess = () => {
    // Reset customization store
    resetCustomization();
  };

  if (!normalizedProductHandle) {
    return <div className="product-customization-drawer__empty">Product data is unavailable.</div>;
  }

  return (
    <div className="product-customization-drawer__container">
      <div className="product-customization-drawer__product-preview">
        <div className="product-customization-drawer__product-preview-content">
          <CEProductCustomizationPreview
            productHandle={normalizedProductHandle}
            productType={normalizedProductType}
            playerHandle={playerHandle}
            competitionHandle={competitionHandle}
            showProductImage={showProductImage}
            isAlwaysVisible={isAlwaysVisible}
            customizationHookConfig={customizationHookConfig}
          />
        </div>
      </div>

      <div className="product-customization-drawer__form">
        <CEProductCustomizationForm customizationHookConfig={customizationHookConfig} />
      </div>

      <div className="product-customization-drawer__footer">
        <CEProductCustomizationPrice
          storeName={storeName}
          language={language}
          locale={locale}
          currencyCode={currency}
        />
        <CEAddToCartButton storeName={storeName} onAddToCartSuccess={handleAddToCartSuccess} language={language} />
      </div>
    </div>
  );
}
